import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

// Persistent banner that prompts the user to install the PWA on supported devices.
// Uses the standard `beforeinstallprompt` event (Chrome/Edge/Samsung Internet).
// Hides itself if dismissed (localStorage flag) or once the app is installed.
const DISMISS_KEY = "pwa_install_dismissed_v1";

export default function InstallPwaPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!deferred || dismissed) return null;

  const handleInstall = async () => {
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch (err) {
      console.debug("Install prompt failed", err);
    } finally {
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-sm rounded-2xl border border-blue-200 bg-white p-3 shadow-lg sm:left-auto sm:right-4"
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Instalar la app en tu dispositivo"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1D4ED8] text-white">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-slate-900">Instalar Ciudad Enfermeria</div>
          <p className="mt-0.5 text-xs text-slate-600">
            Anade la app a tu telefono para acceso rapido y uso sin conexion.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={handleInstall}
              className="h-9 rounded-lg bg-[#1D4ED8] text-sm font-semibold hover:bg-[#1E40AF]"
              data-testid="pwa-install-btn"
            >
              Instalar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="h-9 rounded-lg text-sm font-medium text-slate-600"
              data-testid="pwa-dismiss-btn"
            >
              Ahora no
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
